// Function index:
//   startHexField(canvas) — animate a faint, slowly drifting flat-top hex
//       lattice with a handful of cells that pulse in the signature yellow.
//       Returns stop() to cancel the RAF loop + resize listener.
//
// Why: gives the title screen a "living world" backdrop without pulling in
// Pixi — a single 2D canvas, ~one stroke per visible hex per frame.

const ACCENT = '255, 235, 136'; // --color-accent rgb

export function startHexField(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')!;
  const SIZE = 36;                       // hex circumradius (px)
  const hStep = SIZE * 1.5;              // flat-top column spacing
  const vStep = SIZE * Math.sqrt(3);     // row spacing
  let w = 0, h = 0, dpr = 1, raf = 0;
  const t0 = performance.now();

  const resize = (): void => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth; h = canvas.clientHeight;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const hexPath = (cx: number, cy: number, r: number): void => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 180) * (60 * i);   // flat-top: first vertex at 0°
      const x = cx + r * Math.cos(a);
      const y = cy + r * Math.sin(a);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  };

  const frame = (now: number): void => {
    const t = (now - t0) / 1000;
    const drift = (t * 6) % vStep;        // slow downward drift
    ctx.clearRect(0, 0, w, h);
    let col = 0;
    for (let x = -SIZE; x < w + SIZE; x += hStep) {
      const yOff = (col % 2) * (vStep / 2);
      let row = 0;
      for (let y = -SIZE; y < h + SIZE + vStep; y += vStep) {
        const cx = x;
        const cy = y + yOff + drift;
        hexPath(cx, cy, SIZE - 2);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255,255,255,0.022)';
        ctx.stroke();
        // A few cells glow: a slow sine keyed off grid position + time.
        const phase = Math.sin(t * 0.5 + col * 0.9 + row * 1.4);
        if (phase > 0.9) {
          const g = (phase - 0.9) / 0.1;   // 0..1 ramp
          hexPath(cx, cy, SIZE - 2);
          ctx.lineWidth = 1.4;
          ctx.strokeStyle = `rgba(${ACCENT}, ${0.14 * g})`;
          ctx.stroke();
          ctx.fillStyle = `rgba(${ACCENT}, ${0.05 * g})`;
          ctx.fill();
        }
        row++;
      }
      col++;
    }
    raf = requestAnimationFrame(frame);
  };

  resize();
  window.addEventListener('resize', resize);
  raf = requestAnimationFrame(frame);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}
